import React, { useState } from 'react';
import Yup from '~/util/yup';
import {
	addNonSkilledNursingToLegacyReferralOptions,
	addSkilledNursingToLegacyReferralOptions,
	removeLegacyReferralDisciplineOptions,
	DisciplineEnum,
	DisciplineOptions,
	getLegacyReferralOptions,
	type LegacyReferralViewModel
} from '@generated';
import { FormModal } from '~/shared/forms/FormModal';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { LegacyClinicianAutocomplete } from './assignments/LegacyClinicianAutocomplete';

export function CancelDisciplineFormModal({ legacyReferral, closeModal }: { legacyReferral: LegacyReferralViewModel; closeModal: () => void }) {
	const disciplines = DisciplineOptions.filter(x => legacyReferral.disciplinesNeeded.includes(x.id));

	return (
		<FormModal
			title="Remove Discipline"
			initialValues={{
				referralId: legacyReferral.id,
				disciplineId: null
			}}
			validationSchema={Yup.object().shape({
				disciplineId: Yup.number().label('Discipline').nullable().required()
			})}
			saveMutation={{
				...removeLegacyReferralDisciplineOptions,
				invalidateQueryKeys: [getLegacyReferralOptions(legacyReferral.id).queryKey, getSearchQueryKey('legacy-referral')]
			}}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			<div className="flex flex-col gap-2">
				<p className="mt-2 text-sm text-gray-500">
					This will unassign the discipline from this referral, cancel its outstanding notifications and update the referral status.
				</p>
				<SelectFormField name="disciplineId" options={disciplines} />
			</div>
		</FormModal>
	);
}

export function AddDisciplineFormModal({ legacyReferral, closeModal }: { legacyReferral: LegacyReferralViewModel; closeModal: () => void }) {
	const [disciplineId, setDisciplineId] = useState<number | null>(null);
	const isSkilledNursing = disciplineId === DisciplineEnum.SkilledNursing;
	const disciplines = DisciplineOptions.filter(x => !legacyReferral.disciplinesNeeded.includes(x.id));

	return (
		<FormModal
			title="Add Discipline"
			initialValues={{
				referralId: legacyReferral.id,
				disciplineId: null,
				assignedToId: null,
				assignedToStartOfCareId: null,
				assignedToPrimaryCareId: null
			}}
			validationSchema={Yup.object().shape({
				disciplineId: Yup.number().label('Discipline').nullable().required(),
				assignedToId: Yup.string().label('Assign To').nullable(),
				assignedToStartOfCareId: Yup.string().label('Assign SOC To').nullable(),
				assignedToPrimaryCareId: Yup.string().label('Assign Primary Care To').nullable()
			})}
			onSaveTransform={values =>
				isSkilledNursing
					? {
							referralId: values.referralId,
							assignedToStartOfCareId: values.assignedToStartOfCareId,
							assignedToPrimaryCareId: values.assignedToPrimaryCareId
					  }
					: { referralId: values.referralId, disciplineId: values.disciplineId, assignedToId: values.assignedToId }
			}
			saveMutation={{
				...(isSkilledNursing ? addSkilledNursingToLegacyReferralOptions : addNonSkilledNursingToLegacyReferralOptions),
				invalidateQueryKeys: [getLegacyReferralOptions(legacyReferral.id).queryKey, getSearchQueryKey('legacy-referral')]
			}}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			<div className="flex flex-col gap-4">
				<p className="mt-2 text-sm text-gray-500">Clinicians matching the discipline will be notified. Optionally assign a clinician now.</p>
				<SelectFormField name="disciplineId" options={disciplines} onChangePassThrough={x => setDisciplineId(x ? Number(x) : null)} />
				{disciplineId === null ? null : isSkilledNursing ? (
					<div className="grid grid-cols-2 gap-4">
						<LegacyClinicianAutocomplete name="assignedToStartOfCareId" disciplineId={DisciplineEnum.SkilledNursing} startOfCare />
						<LegacyClinicianAutocomplete name="assignedToPrimaryCareId" disciplineId={DisciplineEnum.SkilledNursing} />
					</div>
				) : (
					<LegacyClinicianAutocomplete key={disciplineId} name="assignedToId" disciplineId={disciplineId} />
				)}
			</div>
		</FormModal>
	);
}
